import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Bike, Building2, ArrowUpRight } from 'lucide-react';

const CARDS = [
  {
    value: 'restaurant',
    icon: UtensilsCrossed,
    titleKey: 'v2_audience_restaurant_title',
    descKey: 'v2_audience_restaurant_desc',
    ctaKey: 'v2_audience_restaurant_cta',
    accent: 'orange',
  },
  {
    value: 'courier',
    icon: Bike,
    titleKey: 'v2_audience_courier_title',
    descKey: 'v2_audience_courier_desc',
    ctaKey: 'v2_audience_courier_cta',
    accent: 'purple',
  },
  {
    value: 'company',
    icon: Building2,
    titleKey: 'v2_audience_company_title',
    descKey: 'v2_audience_company_desc',
    ctaKey: 'v2_audience_company_cta',
    accent: 'navy',
  },
];

const accentStyles = {
  orange: {
    iconBg: 'bg-gradient-to-br from-orange-500/30 to-orange-700/20 border-orange-500/40',
    iconColor: 'text-orange-600 dark:text-orange-400',
    glow: 'group-hover:shadow-[0_0_40px_rgba(255,106,0,0.35)]',
    border: 'hover:border-orange-500/50',
    titleAccent: 'group-hover:text-orange-600 dark:group-hover:text-orange-300',
  },
  purple: {
    iconBg: 'bg-gradient-to-br from-fuchsia-500/30 to-purple-700/20 border-fuchsia-500/40',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-400',
    glow: 'group-hover:shadow-[0_0_40px_rgba(176,38,255,0.4)]',
    border: 'hover:border-fuchsia-500/50',
    titleAccent: 'group-hover:text-fuchsia-600 dark:group-hover:text-fuchsia-300',
  },
  navy: {
    iconBg: 'bg-gradient-to-br from-indigo-500/40 to-blue-700/30 border-indigo-500/50 dark:from-indigo-500/30 dark:to-blue-900/40 dark:border-indigo-400/40',
    iconColor: 'text-indigo-600 dark:text-indigo-300',
    glow: 'group-hover:shadow-[0_0_40px_rgba(99,102,241,0.35)]',
    border: 'hover:border-indigo-500/50',
    titleAccent: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-200',
  },
};

export const AudienceCardsV2 = ({ onApplySelect }) => {
  const { t } = useTranslation();

  return (
    <section id="audiences" className="relative py-24 sm:py-32 overflow-hidden" data-testid="v2-audiences">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] bg-fuchsia-700/10" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mb-14 sm:mb-20"
        >
          <h2 className="font-heading font-bold text-3xl sm:text-5xl lg:text-6xl text-foreground leading-tight tracking-tight">
            {t('v2_audience_title')}
          </h2>
          <p className="mt-5 text-foreground/65 text-base sm:text-lg max-w-2xl">
            {t('v2_audience_sub')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
          {CARDS.map((card, idx) => {
            const Icon = card.icon;
            const s = accentStyles[card.accent];
            return (
              <motion.button
                key={card.value}
                onClick={() => onApplySelect(card.value)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.12 }}
                className={`group relative text-left p-7 sm:p-8 rounded-3xl bg-foreground/[0.04] backdrop-blur border border-border ${s.border} ${s.glow} transition-all duration-500`}
                data-testid={`v2-audience-card-${card.value}`}
              >
                <div className={`w-14 h-14 rounded-2xl border ${s.iconBg} flex items-center justify-center mb-7 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={1.6} />
                </div>

                <h3 className={`font-heading font-bold text-2xl text-foreground ${s.titleAccent} transition-colors duration-300`}>
                  {t(card.titleKey)}
                </h3>
                <p className="mt-3 text-foreground/65 text-sm sm:text-base leading-relaxed">
                  {t(card.descKey)}
                </p>

                <div className="mt-8 flex items-center gap-2 text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">
                  {t(card.ctaKey)}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
